// src/components/TeralehtiEditor.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDate, isPast, isToday, nextDay, parseId } from '../utils/dateUtils';
import MonthCalendar from './ui/MonthCalendar';
import {
  loadDictionary,
  generatePuzzle,
  checkMustWord,
  buildCandidates,
  defaultTier,
  isValidLetters,
  isPangram,
  puzzleStats,
  setKey,
  DEFAULT_THRESHOLDS,
} from '../utils/teralehtiGenerator';
import {
  fetchTeralehtiPuzzles,
  saveTeralehtiPuzzle,
  fetchWordLists,
  updateWordLists,
  saveBonusWords,
} from '../services/teralehtiService';
import TeralehtiWordPeek from './TeralehtiWordPeek';
import WordSheet from './TeralehtiWordSheet';
import useMediaQuery from '../hooks/useMediaQuery';
import UiIcon from './ui/UiIcon';

const REUSE_DAYS = 365;
const THRESHOLD_OPTIONS = [0, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2];
const FLAG_LABELS = {
  slang: 'slangi', dial: 'murre', dated: 'vanhahtava', rare: 'harvinainen', vulg: 'halventava',
  alt: 'kirjoitusmuunnos', stub: 'ei sanakirja-artikkelia', name: 'myös erisnimi', nogloss: 'ei selitystä',
};
// Flags that often mean the word doesn't belong in a puzzle at all; the rest only describe its register.
const SUSPECT_FLAGS = new Set(['name', 'stub', 'alt']);
// Below this many occurrences per million a word gets highlighted as very rare.
const RARE_FREQ = 0.02;
const COLUMNS = [
  { tier: 'main', title: 'Pääsanat' },
  { tier: 'bonus', title: 'Bonussanat' },
  { tier: 'suggest', title: 'Asiasanasto-ehdotukset' },
];
// Which list a word joins when saved in a tier; it leaves the other lists.
const LIST_FOR_TIER = { main: 'main', bonus: 'removed', banned: 'banned', suggest: null };
const EMPTY_LISTS = { removed: new Set(), main: new Set(), banned: new Set() };
const SETTINGS_KEY = 'teralehti-settings';
const PEEK_DELAY_MS = 250;

// Remembers every word the admin moved in this draft, so later puzzles place it the same way.
const listChanges = (lists, touched, tiers) => {
  const changes = { removed: { add: [], remove: [] }, main: { add: [], remove: [] }, banned: { add: [], remove: [] } };
  touched.forEach(word => {
    const target = LIST_FOR_TIER[tiers.get(word)];
    Object.keys(changes).forEach(name => {
      if (name === target && !lists[name].has(word)) changes[name].add.push(word);
      if (name !== target && lists[name].has(word)) changes[name].remove.push(word);
    });
  });
  const next = Object.fromEntries(Object.keys(changes).map(name => {
    const set = new Set(lists[name]);
    changes[name].add.forEach(w => set.add(w));
    changes[name].remove.forEach(w => set.delete(w));
    return [name, set];
  }));
  return { changes, next };
};

const loadSettings = () => {
  try {
    return { ...DEFAULT_THRESHOLDS, sort: 'freq', ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') };
  } catch {
    return { ...DEFAULT_THRESHOLDS, sort: 'freq' };
  }
};

const freqText = (entry) => (entry.freq == null ? 'ei frekvenssiä' : `${entry.estimated ? '~' : ''}${entry.freq} / milj.`);

const compareEntries = (sort) => (a, b) => {
  if (sort === 'freq' && (a.freq ?? -1) !== (b.freq ?? -1)) return (b.freq ?? -1) - (a.freq ?? -1);
  return a.word.localeCompare(b.word, 'fi');
};

const TeralehtiEditor = () => {
  const [dict, setDict] = useState(null);
  const [puzzles, setPuzzles] = useState({});
  const [lists, setLists] = useState(EMPTY_LISTS);
  const [settings, setSettings] = useState(loadSettings);
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [lettersInput, setLettersInput] = useState('');
  // Word every rolled puzzle must contain (empty = any)
  const [mustWord, setMustWord] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const [peek, setPeek] = useState(null);
  // Touch has no hover, right click or drag: a tap opens a sheet with the meaning and the move buttons instead.
  const touch = useMediaQuery('(pointer: coarse)');
  const [sheet, setSheet] = useState(null);
  const [activeTier, setActiveTier] = useState('main');
  const peekTimer = useRef(null);
  const draggedWord = useRef(null);

  const hidePeek = useCallback(() => {
    clearTimeout(peekTimer.current);
    setPeek(null);
  }, []);

  // The rect is read when the card opens, since the word may have moved while the timer ran.
  const schedulePeek = useCallback((el, entry, meta) => {
    clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setPeek({ entry, rect: el.getBoundingClientRect(), meta }), PEEK_DELAY_MS);
  }, []);

  // After a word is moved away, the next one slides under a still cursor and gets no mouseenter
  // until the mouse moves, so look up the word under the cursor once the re-render has painted.
  const peekAt = (x, y) => requestAnimationFrame(() => requestAnimationFrame(() => {
    const el = document.elementFromPoint(x, y)?.closest('.tl-word');
    const entry = el && dict.byWord.get(el.dataset.word);
    if (entry) schedulePeek(el, entry, el.dataset.meta);
  }));

  // The card is fixed-positioned, so any scroll would leave it pointing at the wrong word.
  useEffect(() => {
    window.addEventListener('scroll', hidePeek, true);
    return () => {
      window.removeEventListener('scroll', hidePeek, true);
      clearTimeout(peekTimer.current);
    };
  }, [hidePeek]);

  useEffect(() => {
    Promise.all([loadDictionary(), fetchTeralehtiPuzzles(), fetchWordLists()])
      .then(([d, p, l]) => {
        setDict(d);
        setPuzzles(p);
        setLists(l);
      })
      .catch(e => setError(`Lataus epäonnistui: ${e.message}`));
  }, []);

  const updateSettings = (patch) => setSettings(prev => {
    const next = { ...prev, ...patch };
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
    return next;
  });

  // Letter sets used within REUSE_DAYS of the given date, mapped to the puzzle id that used them.
  const recentSets = useCallback((dateId) => {
    const sets = new Map();
    const cutoff = parseId(dateId);
    Object.values(puzzles).forEach(p => {
      if (p.id === dateId) return;
      const days = Math.abs(parseId(p.id) - cutoff) / 86400000;
      if (days <= REUSE_DAYS) sets.set(setKey(p.letters), p.id);
    });
    return sets;
  }, [puzzles]);

  const usedSetKeys = useCallback((dateId) => new Set(recentSets(dateId).keys()), [recentSets]);

  // A saved puzzle keeps its own word placement; a new draft places words by defaultTier.
  const draftFrom = useCallback((letters, center, saved, wordLists = lists) => {
    const candidates = buildCandidates(dict, letters, center);
    const tiers = new Map(candidates.map(e => {
      if (!saved) return [e.word, defaultTier(e, wordLists, settings)];
      if (saved.words.includes(e.word)) return [e.word, 'main'];
      if ((saved.bonusWords || []).includes(e.word)) return [e.word, 'bonus'];
      return [e.word, wordLists.banned.has(e.word) ? 'banned' : 'suggest'];
    }));
    return { letters, center, candidates, tiers, touched: new Set(), fromSaved: !!saved };
  }, [dict, lists, settings]);

  const roll = useCallback((dateId, { wordLists = lists, extraUsed = [] } = {}) => {
    const used = new Set([...usedSetKeys(dateId), ...extraUsed]);
    const tierOf = e => defaultTier(e, wordLists, settings);
    let mustContain = null;
    if (mustWord.trim()) {
      const check = checkMustWord(dict, mustWord);
      if (check.error) {
        setError(check.error);
        return;
      }
      mustContain = check.entry;
      if (!['main', 'bonus'].includes(tierOf(mustContain))) {
        setError(`Sana "${mustContain.word}" on estetty tai vain ehdotus, joten se ei päätyisi pulmaan`);
        return;
      }
    }
    const result = generatePuzzle(dict, { usedSetKeys: used, tierOf, mustContain });
    if (!result) {
      setError(mustContain ? `Sanalle "${mustContain.word}" ei löytynyt sopivaa kirjainyhdistelmää` : 'Sopivaa kirjainyhdistelmää ei löytynyt');
      return;
    }
    setError(null);
    const next = draftFrom(result.letters, result.center, null, wordLists);
    if (mustContain) {
      // Keep the requested word in the main list, even if it would start as a bonus word
      next.tiers.set(mustContain.word, 'main');
      next.touched.add(mustContain.word);
    }
    setDraft(next);
    setLettersInput(result.letters);
  }, [dict, lists, settings, usedSetKeys, draftFrom, mustWord]);

  const select = useCallback((dateId) => {
    setSelected(dateId);
    setSheet(null);
    const existing = puzzles[dateId];
    if (existing) {
      setDraft(draftFrom(existing.letters, existing.center, existing));
      setLettersInput(existing.letters);
    } else if (isPast(dateId)) {
      setDraft(null);
    } else {
      roll(dateId);
    }
  }, [puzzles, draftFrom, roll]);

  // Threshold changes re-place the words of an unsaved draft that the admin hasn't moved.
  useEffect(() => {
    setDraft(prev => {
      if (!prev || prev.fromSaved) return prev;
      const tiers = new Map(prev.candidates.map(e =>
        [e.word, prev.touched.has(e.word) ? prev.tiers.get(e.word) : defaultTier(e, lists, settings)]));
      return { ...prev, tiers };
    });
  }, [settings, lists]);

  const byTier = useMemo(() => {
    const groups = { main: [], bonus: [], suggest: [], banned: [] };
    if (draft) draft.candidates.forEach(e => groups[draft.tiers.get(e.word)].push(e));
    Object.values(groups).forEach(g => g.sort(compareEntries(settings.sort)));
    return groups;
  }, [draft, settings.sort]);

  const mainWords = useMemo(() => byTier.main.map(e => e.word), [byTier]);
  const stats = useMemo(() => (draft ? puzzleStats(mainWords, draft.letters) : null), [draft, mainWords]);

  const locked = selected && isPast(selected);
  const repeatOf = selected && draft && !locked ? recentSets(selected).get(setKey(draft.letters)) : null;

  const moveWord = (word, tier) => {
    setSheet(null);
    if (locked) return;
    setDraft(prev => {
      if (!prev.tiers.has(word) || prev.tiers.get(word) === tier) return prev;
      const tiers = new Map(prev.tiers).set(word, tier);
      return { ...prev, tiers, touched: new Set(prev.touched).add(word) };
    });
  };

  const onWordClick = (e, entry, tier) => {
    if (e.ctrlKey || e.metaKey) {
      window.open(`https://asiasanasto.fi/sana/${encodeURIComponent(entry.word)}`, '_blank', 'noopener');
      return;
    }
    const next = {
      main: 'bonus',
      bonus: 'main',
      suggest: e.shiftKey ? 'bonus' : 'main',
      banned: entry.src === 'k' ? 'bonus' : 'suggest',
    }[tier];
    moveWord(entry.word, next);
  };

  const onWordContextMenu = (e, entry, tier) => {
    e.preventDefault();
    moveWord(entry.word, tier === 'banned' ? (entry.src === 'k' ? 'bonus' : 'suggest') : 'banned');
  };

  const dropProps = (tier) => ({
    onDragOver: (e) => { if (!locked) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(tier); } },
    onDragLeave: () => setDragOver(null),
    onDrop: (e) => {
      e.preventDefault();
      setDragOver(null);
      moveWord(e.dataTransfer.getData('text/plain') || draggedWord.current, tier);
    },
  });

  const setCenter = (center) => setDraft(prev => draftFrom(prev.letters, center));

  const applyLetters = () => {
    const letters = lettersInput.trim().toLowerCase();
    const center = draft && letters.includes(draft.center) ? draft.center : letters[0];
    if (!isValidLetters(letters, center)) {
      setError('Anna 7 eri kirjainta (a–ö ilman c q w x z å)');
      return;
    }
    setError(null);
    setDraft(draftFrom(letters, center));
  };

  const save = useCallback(async (advance) => {
    if (!draft || !selected || locked || saving) return;
    if (stats.pangrams.length === 0) {
      setError('Pääsanoissa pitää olla vähintään yksi pangrammi');
      return;
    }
    if (isToday(selected) && puzzles[selected] &&
        !window.confirm('Tämän päivän pulma on jo julkaistu. Tallennetaanko silti?')) return;

    setSaving(true);
    try {
      const sortFi = (a, b) => a.localeCompare(b, 'fi');
      const puzzle = {
        id: selected,
        letters: draft.letters,
        center: draft.center,
        words: [...mainWords].sort(sortFi),
        bonusWords: byTier.bonus.map(e => e.word).sort(sortFi),
      };
      const { changes, next: nextLists } = listChanges(lists, draft.touched, draft.tiers);
      await Promise.all([saveTeralehtiPuzzle(puzzle), updateWordLists(changes)]);

      const nextPuzzles = { ...puzzles, [selected]: puzzle };
      setLists(nextLists);
      setPuzzles(nextPuzzles);
      setError(null);

      if (advance) {
        let id = nextDay(selected);
        while (nextPuzzles[id]) id = nextDay(id);
        const date = parseId(id);
        setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
        setSelected(id);
        roll(id, { wordLists: nextLists, extraUsed: [setKey(puzzle.letters)] });
      } else {
        setDraft(draftFrom(puzzle.letters, puzzle.center, puzzle, nextLists));
      }
    } catch (e) {
      setError(`Tallennus epäonnistui: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }, [draft, selected, locked, saving, stats, puzzles, mainWords, byTier, lists, roll, draftFrom]);

  useEffect(() => {
    const onKey = (e) => {
      if (!selected || locked || ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      if (e.code === 'Space' || e.key === 'Enter') document.activeElement?.blur();
      if (e.code === 'Space') {
        e.preventDefault();
        roll(selected);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        save(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, locked, roll, save]);

  const missingBonus = dict ? Object.values(puzzles).filter(p => !Array.isArray(p.bonusWords)) : [];

  const backfillBonusWords = async () => {
    setBackfilling(true);
    try {
      const updated = { ...puzzles };
      for (const p of missingBonus) {
        const bonusWords = buildCandidates(dict, p.letters, p.center)
          .filter(e => e.src === 'k' && !p.words.includes(e.word))
          .map(e => e.word);
        await saveBonusWords(p.id, bonusWords);
        updated[p.id] = { ...p, bonusWords };
      }
      setPuzzles(updated);
      setError(null);
    } catch (e) {
      setError(`Bonussanojen päivitys epäonnistui: ${e.message}`);
    } finally {
      setBackfilling(false);
    }
  };

  // Puzzles saved with words the current dictionary no longer has (adverbs, multi-word fragments) or that were banned since.
  // Covers past puzzles too; once Terälehti is live that would cost players points, as the app drops found words missing from the puzzle.
  const isStale = (w) => !dict.byWord.has(w) || lists.banned.has(w);
  const stalePuzzles = dict
    ? Object.values(puzzles).filter(p => [...p.words, ...(p.bonusWords || [])].some(isStale))
    : [];

  const removeStaleWords = async () => {
    setCleaning(true);
    try {
      const updated = { ...puzzles };
      const noPangram = [];
      for (const p of stalePuzzles) {
        const words = p.words.filter(w => !isStale(w));
        if (!words.some(w => isPangram(w, p.letters))) {
          noPangram.push(p.id);
          continue;
        }
        const puzzle = { ...p, words, bonusWords: (p.bonusWords || []).filter(w => !isStale(w)) };
        await saveTeralehtiPuzzle(puzzle);
        updated[p.id] = puzzle;
      }
      setPuzzles(updated);
      setError(noPangram.length
        ? `Näistä pulmista jäisi pangrammi pois, arvo ne uudelleen: ${noPangram.join(', ')}`
        : null);
    } catch (e) {
      setError(`Sanojen poisto epäonnistui: ${e.message}`);
    } finally {
      setCleaning(false);
    }
  };

  if (error && !dict) return <div className="tl-error">{error}</div>;
  if (!dict) return <div>Ladataan sanakirjaa...</div>;


  const firstEmpty = () => {
    let id = formatDate(new Date());
    while (puzzles[id]) id = nextDay(id);
    return id;
  };

  const renderWord = (entry, tier) => {
    const flags = entry.flags.filter(f => FLAG_LABELS[f]);
    // Red dot = probably doesn't belong; grey = register note, only worth showing on Asiasanasto words.
    // Derogatory words get a dotted red outline instead (they stay where their frequency puts them).
    const dot = flags.some(f => SUSPECT_FLAGS.has(f)) ? 'suspect'
      : entry.src === 'a' && flags.some(f => f !== 'nogloss') ? 'register' : null;
    const cls = [
      'tl-word',
      // Unknown frequency fades the word (usually junk); in main words it's also rare, so it still stands out there.
      entry.freq == null ? 'unknown' : '',
      entry.freq < RARE_FREQ && (entry.freq != null || tier === 'main') ? 'rare' : '',
      flags.includes('vulg') ? 'vulg' : '',
      entry.src === 'a' && tier !== 'suggest' ? 'asia' : '',
      isPangram(entry.word, draft.letters) ? 'pangram' : '',
    ].join(' ');
    const meta = [freqText(entry), ...(entry.src === 'a' ? ['Asiasanasto'] : []), ...flags.map(f => FLAG_LABELS[f])].join(' · ');
    return (
      <button
        key={entry.word}
        className={cls}
        data-word={entry.word}
        data-meta={meta}
        draggable={!locked && !touch}
        onMouseEnter={e => !touch && schedulePeek(e.currentTarget, entry, meta)}
        onMouseLeave={hidePeek}
        onDragStart={e => {
          draggedWord.current = entry.word;
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', entry.word);
          // Re-rendering inside dragstart can make the browser cancel the drag.
          setTimeout(hidePeek, 0);
        }}
        onDragEnd={() => { draggedWord.current = null; setDragOver(null); }}
        onClick={e => {
          hidePeek();
          if (touch) { setSheet({ entry, tier, meta }); return; }
          onWordClick(e, entry, tier);
          peekAt(e.clientX, e.clientY);
        }}
        onContextMenu={e => { hidePeek(); onWordContextMenu(e, entry, tier); peekAt(e.clientX, e.clientY); }}
      >
        {entry.word}
        {dot && <span className={`tl-dot ${dot}`} />}
      </button>
    );
  };

  return (
    <div className="tl-container">
      <MonthCalendar month={month} onMonthChange={setMonth} selectedId={selected} renderDay={(id, n) => {
            const p = puzzles[id];
            const cls = ['tl-day', p ? 'filled' : 'empty', isToday(id) ? 'today' : '', isPast(id) ? 'past' : '', id === selected ? 'selected' : ''].join(' ');
            return (
              <button key={id} className={cls} onClick={() => select(id)}>
                <span className="tl-day-number">{n}</span>
                {p && <span className="tl-day-letters">{p.center.toUpperCase()}</span>}
              </button>
            );
          }}>
        <button className="btn-primary tl-first-empty" onClick={() => select(firstEmpty())}>
          Seuraava tyhjä päivä
        </button>
        {missingBonus.length > 0 && (
          <button className="btn-secondary tl-first-empty" onClick={backfillBonusWords} disabled={backfilling}>
            {backfilling ? 'Päivitetään...' : `Lisää bonussanat ${missingBonus.length} pulmaan`}
          </button>
        )}
        {stalePuzzles.length > 0 && (
          <button className="btn-secondary tl-first-empty" onClick={removeStaleWords} disabled={cleaning}
            title="Poistaa pulmista sanat, joita sanalistassa ei enää ole (esim. adverbit) tai jotka on siirretty roskakoriin">
            {cleaning ? 'Poistetaan...' : `Poista vanhentuneet sanat ${stalePuzzles.length} pulmasta`}
          </button>
        )}

        <div className="tl-settings">
          <label>
            Kotus-sana bonukseksi, jos alle
            <select value={settings.kotusMin} onChange={e => updateSettings({ kotusMin: Number(e.target.value) })}>
              {THRESHOLD_OPTIONS.map(v => <option key={v} value={v}>{v} / milj.</option>)}
            </select>
          </label>
          <label>
            Asiasanasto-sana bonukseksi, jos väh.
            <select value={settings.asiaMin} onChange={e => updateSettings({ asiaMin: Number(e.target.value) })}>
              {THRESHOLD_OPTIONS.filter(v => v > 0).map(v => <option key={v} value={v}>{v} / milj.</option>)}
            </select>
          </label>
          <label>
            Järjestys
            <select value={settings.sort} onChange={e => updateSettings({ sort: e.target.value })}>
              <option value="freq">Yleisyys</option>
              <option value="alpha">Aakkoset</option>
            </select>
          </label>
          <p className="tl-hint">Kynnykset vaikuttavat uusiin arvontoihin ja tallentamattomaan luonnokseen. Käsin siirretyt sanat muistetaan.</p>
        </div>
      </MonthCalendar>

      <section className="tl-editor">
        {!selected || !draft ? (
          <p className="tl-hint">Valitse päivä. Välilyönti = arvo uusi, Enter = tallenna ja seuraava.</p>
        ) : (
          <>
            <div className="tl-editor-header">
              <h2>{selected}{puzzles[selected] ? '' : ' (uusi)'}</h2>
              {!locked && (
                <input
                  className="tl-must-word"
                  value={mustWord}
                  onChange={e => setMustWord(e.target.value.toLowerCase())}
                  onKeyDown={e => e.key === 'Enter' && (e.stopPropagation(), roll(selected), e.target.blur())}
                  placeholder="Pulmassa sana…"
                  title="Arvo vain pulmia, joissa on tämä sana (enintään 7 eri kirjainta). Tyhjä = mikä tahansa."
                  spellCheck={false}
                />
              )}
              {!locked && (
                <div className="tl-actions">
                  <button className="btn-secondary" onClick={() => roll(selected)}><UiIcon name="casino" /> Arvo <kbd>Space</kbd></button>
                  <button className="btn-secondary" onClick={() => save(false)} disabled={saving}>Tallenna</button>
                  <button className="btn-primary" onClick={() => save(true)} disabled={saving}>Tallenna ja seuraava <kbd>Enter</kbd></button>
                </div>
              )}
            </div>

            {error && <div className="tl-error">{error}</div>}
            {locked && <div className="tl-hint">Mennyt pulma, vain luku.</div>}
            {repeatOf && <div className="tl-error">Samat kirjaimet käytettiin jo {repeatOf}. Arvo uusi, ettei pulma toistu.</div>}

            <div className="tl-letters-row">
              <div className="tl-letters">
                {[...draft.letters].map(c => (
                  <button
                    key={c}
                    className={`tl-letter ${c === draft.center ? 'center' : ''}`}
                    onClick={() => !locked && setCenter(c)}
                    title="Aseta keskikirjaimeksi"
                  >
                    {c.toUpperCase()}
                  </button>
                ))}
              </div>
              {!locked && (
                <div className="tl-manual">
                  <input
                    value={lettersInput}
                    onChange={e => setLettersInput(e.target.value.toLowerCase())}
                    onKeyDown={e => e.key === 'Enter' && (applyLetters(), e.target.blur())}
                    maxLength={7}
                    placeholder="7 kirjainta"
                  />
                  <button className="btn-secondary" onClick={applyLetters}>Käytä</button>
                </div>
              )}
            </div>

            <div className="tl-stats">
              <span className={stats.count < 15 || stats.count > 70 ? 'warn' : ''}>{stats.count} sanaa</span>
              <span className={stats.pangrams.length === 0 ? 'warn' : ''}>{stats.pangrams.length} pangrammia</span>
              <span>max {stats.maxScore} p</span>
              <span>ilmaisraja (Mainio) {stats.freeCapScore} p</span>
            </div>

            <div className="tl-tier-tabs" role="tablist">
              {COLUMNS.map(({ tier, title }) => (
                <button key={tier} role="tab" aria-selected={activeTier === tier}
                  className={`tl-tier-tab ${activeTier === tier ? 'active' : ''}`} onClick={() => setActiveTier(tier)}>
                  {title.replace('Asiasanasto-', '')} <span>{byTier[tier].length}</span>
                </button>
              ))}
            </div>

            <div className="tl-columns">
              {COLUMNS.map(({ tier, title }) => (
                <div key={tier} className={`tl-column ${activeTier === tier ? 'tab-active' : ''} ${dragOver === tier ? 'drag-over' : ''}`} {...dropProps(tier)}>
                  <h4>{title}<span>{byTier[tier].length}</span></h4>
                  <div className="tl-words">{byTier[tier].map(e => renderWord(e, tier))}</div>
                </div>
              ))}
            </div>

            <div className={`tl-trash ${dragOver === 'banned' ? 'drag-over' : ''}`} {...dropProps('banned')}>
              <h4>Roskakori<span>{byTier.banned.length || (touch ? '' : 'raahaa tai oikea klikkaus')}</span></h4>
              {byTier.banned.length > 0 && <div className="tl-words">{byTier.banned.map(e => renderWord(e, 'banned'))}</div>}
            </div>

            <div className="tl-legend">
              <span className="unknown-text">harvinainen tai ei yleisyystietoa</span>
              <span className="vulg-text">halventava</span>
              <span><i className="tl-dot suspect" />nimi tai tynkä</span>
              <span><i className="tl-dot register" />slangi, murre tai vanhahtava</span>
              <span className="asia-text">Asiasanastosta</span>
              <span className="tl-legend-keys">{touch
                ? 'Napauta sanaa: merkitys ja siirtopainikkeet'
                : 'Shift+klikkaus ehdotuksesta bonukseksi · Ctrl+klikkaus avaa sanan · määritelmä hiiren alla'}</span>
            </div>
            {peek && !touch && <TeralehtiWordPeek {...peek} />}
            {sheet && (
              <WordSheet
                {...sheet}
                locked={locked}
                onMove={tier => moveWord(sheet.entry.word, tier)}
                onClose={() => setSheet(null)}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default TeralehtiEditor;
